import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["searchInput", "optionItem", "hiddenValue", "hiddenCheckbox", "selectedSummary", "optionsList", "toggleIcon"]
  static values = { mode: String }

  connect() {
    this.handleDocumentClick = this.handleDocumentClick.bind(this)
    this.handleFormSubmit = this.handleFormSubmit.bind(this)
    document.addEventListener("click", this.handleDocumentClick)
    this.formElement = this.element.closest("form")
    if (this.formElement) this.formElement.addEventListener("submit", this.handleFormSubmit)
    this.setListOpen(false)
    this.updateSelectedSummary()
  }

  disconnect() {
    document.removeEventListener("click", this.handleDocumentClick)
    if (this.formElement) this.formElement.removeEventListener("submit", this.handleFormSubmit)
  }

  toggleList(event) {
    if (event) event.preventDefault()
    this.setListOpen(this.isClosed())
  }

  openList() {
    this.setListOpen(true)
  }

  filter() {
    const query = this.hasSearchInputTarget ? this.searchInputTarget.value.toLowerCase().trim() : ""
    this.setListOpen(true)
    this.optionItemTargets.forEach((item) => {
      const label = (item.dataset.optionLabel || "").toLowerCase()
      item.classList.toggle("d-none", query.length > 0 && !label.includes(query))
    })
  }

  selectSingle(event) {
    if (this.modeValue !== "single") return

    const item = event.currentTarget
    const value = item.dataset.optionValue || ""
    const text = item.dataset.optionText || ""

    if (this.hasHiddenValueTarget) this.hiddenValueTarget.value = value
    if (this.hasSearchInputTarget) {
      this.searchInputTarget.value = text
      this.searchInputTarget.setCustomValidity("")
    }

    this.optionItemTargets.forEach((opt) => {
      opt.classList.remove("fw-bold")
      const indicator = opt.querySelector("[data-selected-indicator='true']")
      if (indicator) indicator.classList.add("d-none")
    })
    item.classList.add("fw-bold")
    const selectedIndicator = item.querySelector("[data-selected-indicator='true']")
    if (selectedIndicator) selectedIndicator.classList.remove("d-none")
    this.setListOpen(false)
  }

  toggleMulti(event) {
    if (this.modeValue !== "multiple") return

    const item = event.currentTarget
    const value = item.dataset.optionValue || ""
    const checkbox = this.hiddenCheckboxTargets.find((el) => el.dataset.optionValue === value)
    if (!checkbox) return

    checkbox.checked = !checkbox.checked
    item.classList.toggle("fw-bold", checkbox.checked)

    const indicator = item.querySelector("[data-selected-indicator='true']")
    if (indicator) indicator.classList.toggle("d-none", !checkbox.checked)

    this.updateSelectedSummary()
  }

  updateSelectedSummary() {
    if (!this.hasSelectedSummaryTarget || this.modeValue !== "multiple") return

    const selectedLabels = this.optionItemTargets
      .filter((item) => item.classList.contains("fw-bold"))
      .map((item) => item.dataset.optionText)
      .filter(Boolean)

    this.selectedSummaryTarget.textContent =
      selectedLabels.length > 0 ? `Selected: ${selectedLabels.join(", ")}` : "No options selected"
  }

  isClosed() {
    return this.hasOptionsListTarget && this.optionsListTarget.classList.contains("d-none")
  }

  setListOpen(open) {
    if (this.hasOptionsListTarget) this.optionsListTarget.classList.toggle("d-none", !open)
    if (this.hasToggleIconTarget) this.toggleIconTarget.classList.toggle("fa-chevron-up", open)
    if (this.hasToggleIconTarget) this.toggleIconTarget.classList.toggle("fa-chevron-down", !open)
  }

  handleDocumentClick(event) {
    if (!this.element.contains(event.target)) this.setListOpen(false)
  }

  handleFormSubmit(event) {
    if (this.modeValue !== "single") return
    if (!this.hasSearchInputTarget || !this.hasHiddenValueTarget) return

    const typed = (this.searchInputTarget.value || "").trim()
    const hiddenValue = (this.hiddenValueTarget.value || "").trim()
    if (!typed && !hiddenValue) return

    const selectedItem = this.optionItemTargets.find((item) => (item.dataset.optionValue || "") === hiddenValue)
    const selectedLabel = (selectedItem?.dataset?.optionText || "").trim().toLowerCase()
    const typedLabel = typed.toLowerCase()

    const valid = hiddenValue.length > 0 && selectedLabel.length > 0 && selectedLabel === typedLabel
    if (valid) {
      this.searchInputTarget.setCustomValidity("")
      return
    }

    event.preventDefault()
    this.hiddenValueTarget.value = ""
    this.searchInputTarget.setCustomValidity("Please select a valid option from the dropdown list.")
    this.searchInputTarget.reportValidity()
    this.openList()
  }
}
